import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { prefersReducedMotion } from './countUp'
import styles from './BriefLoading.module.css'

/*
 * While a brief is being generated: one line icon that morphs from stage to stage, starting from Multiverse's own
 * mark (its globe, ring and crescent), with a sentence saying what's under way. Every icon is drawn in the mark's
 * three colours. The stages are paced by time, not by the server: they step on every couple of seconds and the
 * last holds until the brief arrives.
 *
 * Every icon is drawn with the same four paths, and each path is evened out to the same number of curves in every
 * icon, so each can morph straight into the next icon's (SMIL, which every browser animates).
 */

type Point = [number, number]
type Cubic = [number, number, number, number, number, number]
/** a straight line to a point, a rounded corner (its corner, then where it ends) or a curve */
type Seg = Point | [number, number, number, number] | Cubic

/* how far along a corner's sides a quarter circle's control points sit */
const ARC = 0.5523

const toCubics = (start: Point, segs: Seg[]): Cubic[] => {
  let [px, py] = start
  return segs.map(seg => {
    let cubic: Cubic
    if (seg.length === 2) {
      cubic = [
        px + (seg[0] - px) / 3,
        py + (seg[1] - py) / 3,
        px + (2 * (seg[0] - px)) / 3,
        py + (2 * (seg[1] - py)) / 3,
        seg[0],
        seg[1],
      ]
    } else if (seg.length === 4) {
      const [cx, cy, x, y] = seg
      cubic = [px + ARC * (cx - px), py + ARC * (cy - py), x + ARC * (cx - x), y + ARC * (cy - y), x, y]
    } else {
      cubic = seg
    }
    ;[px, py] = [cubic[4], cubic[5]]
    return cubic
  })
}

/* halves the longest curves until there are `count` of them, so every icon's path has the same make-up */
const evenOut = (start: Point, cubics: Cubic[], count: number): Cubic[] => {
  const out = [...cubics]
  while (out.length < count) {
    let longest = 0
    let length = -1
    let [px, py] = start
    out.forEach((c, i) => {
      const l =
        Math.hypot(c[0] - px, c[1] - py) + Math.hypot(c[2] - c[0], c[3] - c[1]) + Math.hypot(c[4] - c[2], c[5] - c[3])
      if (l > length) {
        length = l
        longest = i
      }
      ;[px, py] = [c[4], c[5]]
    })
    const [ax, ay] = longest === 0 ? start : [out[longest - 1][4], out[longest - 1][5]]
    const [b1x, b1y, b2x, b2y, dx, dy] = out[longest]
    const mid = (a: number, b: number) => (a + b) / 2
    const [e1x, e1y, e2x, e2y, e3x, e3y] = [
      mid(ax, b1x),
      mid(ay, b1y),
      mid(b1x, b2x),
      mid(b1y, b2y),
      mid(b2x, dx),
      mid(b2y, dy),
    ]
    const [f1x, f1y, f2x, f2y] = [mid(e1x, e2x), mid(e1y, e2y), mid(e2x, e3x), mid(e2y, e3y)]
    const [gx, gy] = [mid(f1x, f2x), mid(f1y, f2y)]
    out.splice(longest, 1, [e1x, e1y, f1x, f1y, gx, gy], [f2x, f2y, e3x, e3y, dx, dy])
  }
  return out
}

/* how many curves each of the four paths has, in every icon */
const CURVES = [10, 8, 4, 2]

const draw = (start: Point, segs: Seg[], path: number): string => {
  const round = (n: number) => Math.round(n * 100) / 100
  return `M${start[0]} ${start[1]}${evenOut(start, toCubics(start, segs), CURVES[path])
    .map(c => `C${c.map(round).join(' ')}`)
    .join('')}`
}

/* a rounded box, starting at the top left (after its corner) */
const box = (x1: number, y1: number, x2: number, y2: number, r: number): [Point, Seg[]] => [
  [x1 + r, y1],
  [
    [x2 - r, y1],
    [x2, y1, x2, y1 + r],
    [x2, y2 - r],
    [x2, y2, x2 - r, y2],
    [x1 + r, y2],
    [x1, y2, x1, y2 - r],
    [x1, y1 + r],
    [x1, y1, x1 + r, y1],
  ],
]

/* the same, starting at the bottom left and going round the same way as the printer's body, so one morphs
   cleanly into the other */
const boxFromBottom = (x1: number, y1: number, x2: number, y2: number, r: number): [Point, Seg[]] => [
  [x1 + r, y2],
  [
    [x1, y2, x1, y2 - r],
    [x1, y1 + r],
    [x1, y1, x1 + r, y1],
    [x2 - r, y1],
    [x2, y1, x2, y1 + r],
    [x2, y2 - r],
    [x2, y2, x2 - r, y2],
    [x1 + r, y2],
  ],
]

type Shape = [Point, Seg[]]
const icon = (shapes: [Shape, Shape, Shape, Shape]) =>
  shapes.map(([start, segs], i) => draw(start, segs, i)) as [string, string, string, string]

/** a dot, as a line going nowhere */
const dot = (p: Point): Shape => [p, [p]]

interface Stage {
  text: string
  /** the icon's four paths, in a 48 × 48 box */
  paths: [string, string, string, string]
  /** their colours, from Multiverse's mark (green, lime, teal and teal, if not given) */
  colours?: [string, string, string, string]
}

const GREEN = '#27aa27'
const LIME = '#8ec640'
const TEAL = '#32c2af'
const COLOURS: [string, string, string, string] = [GREEN, LIME, TEAL, TEAL]

const STAGES: Stage[] = [
  {
    // Multiverse's mark: its globe, the ring round it (coming out from behind the globe) and the crescent under it
    text: 'Connecting to your customer in Multiverse',
    paths: icon([
      [
        [12.73, 30.99],
        [
          [10.77, 27.9, 10.19, 24.14, 11.14, 20.61],
          [12.1, 17.08, 14.48, 14.11, 17.73, 12.42],
          [20.97, 10.74, 24.78, 10.49, 28.21, 11.74],
          [31.65, 12.99, 34.4, 15.63, 35.8, 19.01],
        ],
      ],
      [
        [35.23, 13.15],
        [
          [41.89, 11.42, 45.43, 12.34, 43.26, 15.95],
          [40.29, 20.89, 27.93, 28.66, 16.83, 32.57],
          [5.73, 36.47, 0.86, 34.76, 6.41, 28.91],
        ],
      ],
      [[22.04, 36.94], [[27.94, 37.71, 33.61, 34.4, 35.84, 28.89]]],
      [[22.04, 36.94], [[27.94, 37.71, 33.61, 34.4, 35.84, 28.89]]],
    ]),
  },
  {
    // a printer: its body (open where the page comes out), the page, the paper going in at the top and its light
    text: 'Gathering their fleet and print activity',
    paths: icon([
      [
        [12, 36],
        [
          [8, 36],
          [4, 36, 4, 32],
          [4, 22],
          [4, 18, 8, 18],
          [40, 18],
          [44, 18, 44, 22],
          [44, 32],
          [44, 36, 40, 36],
          [36, 36],
        ],
      ],
      box(16, 28, 32, 43, 1),
      [
        [14, 14],
        [
          [14, 4],
          [34, 4],
          [34, 14],
        ],
      ],
      dot([38, 24]),
    ]),
  },
  {
    // a chart: its frame, the trend rising to an arrowhead (square to the trend's last stretch; both lime, so they
    // meet without a clash) and the axis under it
    text: 'Looking for patterns and changes',
    paths: icon([
      boxFromBottom(8, 8, 40, 40, 4),
      [
        [14, 29],
        [
          [20, 22],
          [26, 26],
          [34, 15],
          [34.94, 20.93],
        ],
      ],
      [[28.07, 15.94], [[34, 15]]],
      [[14, 34], [[34, 34]]],
    ]),
    colours: [GREEN, LIME, LIME, TEAL],
  },
  {
    // the AI's stars: a big one, a small one and a speck (the stars drawn from the bottom, going clockwise like the
    // shapes either side of them)
    text: 'Writing up what it all means with AI',
    paths: icon([
      [
        [22, 42],
        [
          [21, 30, 18, 27, 8, 26],
          [18, 25, 21, 22, 22, 10],
          [23, 22, 26, 25, 36, 26],
          [26, 27, 23, 30, 22, 42],
        ],
      ],
      [
        [37, 19],
        [
          [36.4, 13.6, 35.4, 12.6, 30, 12],
          [35.4, 11.4, 36.4, 10.4, 37, 5],
          [37.6, 10.4, 38.6, 11.4, 44, 12],
          [38.6, 12.6, 37.6, 13.6, 37, 19],
        ],
      ],
      dot([40, 35]),
      dot([40, 35]),
    ]),
  },
  {
    // the brief: its page (stopping short of the corner), the folded corner and two lines of writing
    text: 'Putting your brief together',
    paths: icon([
      [
        [37, 19],
        [
          [37, 41],
          [12, 41],
          [12, 7],
          [25, 7],
        ],
      ],
      [
        [29, 7],
        [
          [29, 15],
          [37, 15],
          [29, 7],
        ],
      ],
      [[17, 24], [[31, 24]]],
      [[17, 31], [[27, 31]]],
    ]),
  },
]

/* each stage holds a while (a brief can take some time) and the last holds until the brief arrives */
const STEP_MS = 3600
const MORPH_S = 0.9
/* the paths set off one after another, so the icon flows from one shape to the next */
const STAGGER_S = 0.06

/* a path part way between two shapes (or past the second, for the spring's overshoot): every icon's paths have the
   same make-up, so their numbers pair up one to one */
const between = (from: string, to: string, t: number): string => {
  const a = from.match(/-?[\d.]+/g) ?? []
  let i = 0
  return to.replace(/-?[\d.]+/g, n => {
    const value = Number(a[i]) + (Number(n) - Number(a[i])) * t
    i += 1
    return String(Math.round(value * 100) / 100)
  })
}

/* one of the icon's paths, springing from the last stage's shape to this one's: it overshoots a little, settles
   back and comes to rest. The path sits on the old shape until its turn comes (the animation is new each stage,
   keyed by its target, and only holds the new shape once it has run). */
const MorphPath: FunctionComponent<{ from: string; to: string; colour: string; delay: number }> = ({
  from,
  to,
  colour,
  delay,
}) => {
  const animate = useRef<SVGAnimateElement>(null)
  useEffect(() => {
    const el = animate.current as (SVGAnimateElement & { beginElementAt?: (offset: number) => void }) | null
    if (from !== to && el && typeof el.beginElementAt === 'function') el.beginElementAt(delay)
  }, [from, to, delay])
  return (
    <path d={from} stroke={colour} pathLength={1}>
      {from !== to && (
        <animate
          key={to}
          ref={animate}
          attributeName='d'
          values={[from, between(from, to, 1.1), between(from, to, 0.97), to].join(';')}
          dur={`${MORPH_S}s`}
          begin='indefinite'
          fill='freeze'
          calcMode='spline'
          keyTimes='0;0.5;0.78;1'
          keySplines='0.3 0 0.3 1;0.4 0 0.6 1;0.4 0 0.6 1'
        />
      )}
    </path>
  )
}

/* each icon tips over as it morphs in, then rocks back and forth a little less each time until it settles */
const ARRIVE: Keyframe[] = [
  { transform: 'none' },
  { transform: 'rotate(-10deg) scale(0.9)', offset: 0.25 },
  { transform: 'rotate(6deg) scale(1.05)', offset: 0.5 },
  { transform: 'rotate(-3deg) scale(0.98)', offset: 0.7 },
  { transform: 'rotate(1deg) scale(1.01)', offset: 0.86 },
  { transform: 'none' },
]

export const BriefLoading: FunctionComponent = () => {
  const [step, setStep] = useState(0)
  const previous = useRef(0)
  const svg = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = window.setInterval(() => setStep(s => Math.min(s + 1, STAGES.length - 1)), STEP_MS)
    return () => window.clearInterval(timer)
  }, [])

  const from = STAGES[previous.current].paths
  useEffect(() => {
    if (step !== previous.current && svg.current && typeof svg.current.animate === 'function') {
      svg.current.animate(ARRIVE, { duration: 1100, easing: 'ease-out' })
    }
    previous.current = step
  }, [step])

  const stage = STAGES[step]
  const last = step === STAGES.length - 1
  return (
    <div className={styles.wrap} role='status' aria-live='polite'>
      {/* on the last stage, the brief's lines keep writing themselves out until it arrives */}
      <svg
        ref={svg}
        viewBox='0 0 48 48'
        className={`${styles.icon}${last ? ` ${styles.writing}` : ''}`}
        aria-hidden='true'
      >
        {stage.paths.map((d, i) => (
          <MorphPath
            // eslint-disable-next-line react/no-array-index-key -- the icon's paths, in a fixed order
            key={i}
            from={from[i]}
            to={d}
            colour={(stage.colours ?? COLOURS)[i]}
            delay={i * STAGGER_S}
          />
        ))}
      </svg>
      <p key={step} className={styles.text}>
        {stage.text}
        <span className={styles.dots} aria-hidden='true'>
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </p>
    </div>
  )
}
